import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Monitor } from 'lucide-react';
import { ConfirmModal } from '../ui/Modal';
import { isTauri } from '../../utils/tauri';
import { usePreferencesStore } from '../../store/preferencesStore';
import { useMailCatcherStore } from '../../store/mailCatcherStore';
import { subscribe } from '../../services/mailCatcherService';
import MailCatcherToolbar from './MailCatcherToolbar';
import MailCatcherList from './MailCatcherList';
import MailCatcherDetail from './MailCatcherDetail';
import './MailCatcher.css';

/** The titlebar trash action (ExtensionTitleBarActions) dispatches this. */
export const CLEAR_EVENT = 'mail-catcher-clear';

function MailCatcherPage() {
  const { t } = useTranslation();
  const status = useMailCatcherStore((s) => s.status);
  const starting = useMailCatcherStore((s) => s.starting);
  const messages = useMailCatcherStore((s) => s.messages);
  const total = useMailCatcherStore((s) => s.total);
  const unread = useMailCatcherStore((s) => s.unread);
  const query = useMailCatcherStore((s) => s.query);
  const selectedId = useMailCatcherStore((s) => s.selectedId);
  const detail = useMailCatcherStore((s) => s.detail);
  const detailLoading = useMailCatcherStore((s) => s.detailLoading);
  const detailTab = useMailCatcherStore((s) => s.detailTab);
  const raw = useMailCatcherStore((s) => s.raw);
  const error = useMailCatcherStore((s) => s.error);
  const preferredPort = usePreferencesStore((s) => s.mailCatcherPort);

  // { kind: 'clear' } | { kind: 'delete', id }
  const [confirm, setConfirm] = useState(null);
  // List filter chip (All / Unread / Attachments) — view state, client-side.
  const [filter, setFilter] = useState('all');
  const searchRef = useRef(null);

  // ⌘F / Ctrl+F focuses the search (the hint printed in the box). It was ⌘K,
  // which the global command palette always took first (card 50122def); F is
  // the find key, and the DB grid scopes it to its panel the same way.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && String(e.key).toLowerCase() === 'f') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Server truth is Rust: re-sync on mount, then follow the two events.
  useEffect(() => {
    const store = useMailCatcherStore.getState();
    store.refreshStatus();
    store.load();
    const sub = subscribe({
      onMessage: (summary) => useMailCatcherStore.getState().onIncoming(summary),
      onStatus: (next) => useMailCatcherStore.getState().onStatus(next),
    });
    return () => sub.unlisten();
  }, []);

  const requestClear = useCallback(() => {
    if (useMailCatcherStore.getState().total > 0) setConfirm({ kind: 'clear' });
  }, []);

  useEffect(() => {
    window.addEventListener(CLEAR_EVENT, requestClear);
    return () => window.removeEventListener(CLEAR_EVENT, requestClear);
  }, [requestClear]);

  const requestDelete = useCallback((id) => setConfirm({ kind: 'delete', id }), []);

  const onConfirm = () => {
    const store = useMailCatcherStore.getState();
    if (confirm?.kind === 'clear') store.clear();
    else if (confirm?.kind === 'delete') store.remove([confirm.id]);
  };

  return (
    <div className="mailc-view" data-testid="mail-catcher-view">
      <MailCatcherToolbar
        status={status}
        starting={starting}
        preferredPort={preferredPort}
        query={query}
        total={total}
        unread={unread}
        searchRef={searchRef}
        onStart={() => useMailCatcherStore.getState().start(preferredPort)}
        onStop={() => useMailCatcherStore.getState().stop()}
        onQueryChange={(q) => useMailCatcherStore.getState().setQuery(q)}
        onClearAll={requestClear}
      />
      {error && (
        <div className="mailc-notice mailc-notice--error" role="alert">
          {t('mailCatcher.loadFailed', { error })}
        </div>
      )}
      <div className="mailc-split">
        <div className="mailc-pane-list">
          <MailCatcherList
            messages={messages}
            selectedId={selectedId}
            query={query}
            filter={filter}
            onFilterChange={setFilter}
            port={status.running && status.port ? status.port : preferredPort}
            onSelect={(id) => useMailCatcherStore.getState().select(id)}
            onDeleteRequest={requestDelete}
          />
        </div>
        <div className="mailc-pane-detail">
          <MailCatcherDetail
            detail={detail}
            loading={detailLoading}
            tab={detailTab}
            raw={raw}
            onTab={(tab) => useMailCatcherStore.getState().setTab(tab)}
            onDelete={requestDelete}
          />
        </div>
      </div>
      <ConfirmModal
        isOpen={confirm !== null}
        onClose={() => setConfirm(null)}
        onConfirm={onConfirm}
        title={confirm?.kind === 'clear' ? t('mailCatcher.clearAll') : t('mailCatcher.delete')}
        message={
          confirm?.kind === 'clear'
            ? t('mailCatcher.clearAllConfirm', { count: total })
            : t('mailCatcher.deleteConfirm')
        }
        confirmText={confirm?.kind === 'clear' ? t('mailCatcher.clearAll') : t('mailCatcher.delete')}
        type="danger"
      />
    </div>
  );
}

/**
 * Mail catcher page (App.js `activeTab === 'mail-catcher.panel'`). Full-bleed
 * two-pane mail client; the SMTP server lives in Rust, so this is a browser
 * over its SQLite store. Non-Tauri builds show the desktop-only ghost.
 */
export default function MailCatcherView() {
  const { t } = useTranslation();
  if (!isTauri()) {
    return (
      <div className="mailc-view">
        <div className="mailc-list-empty">
          <div className="mailc-list-empty-icon">
            <Monitor size={24} />
          </div>
          <span className="mailc-list-empty-title">{t('mailCatcher.desktopOnly')}</span>
        </div>
      </div>
    );
  }
  return <MailCatcherPage />;
}
